import { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, DollarSign, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const expenseCategories = ['Ingredientes', 'Personal', 'Arriendo', 'Servicios', 'Marketing', 'Empaques', 'Transporte', 'Equipos', 'Otros'];

export default function AdminFinances() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [txnType, setTxnType] = useState('payment');
  const [filterType, setFilterType] = useState('all');
  const [form, setForm] = useState({ amount: 0, description: '', category: 'general', reference: '' });

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sumRes, txnRes] = await Promise.all([
        axios.get(`${API}/admin/finances/summary`, { withCredentials: true, headers: getHeaders() }),
        axios.get(`${API}/admin/finances/transactions`, { withCredentials: true, headers: getHeaders(), params: filterType !== 'all' ? { type: filterType } : {} })
      ]);
      setSummary(sumRes.data);
      setTransactions(txnRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterType]);

  const openDialog = (type) => { setTxnType(type); setForm({ amount: 0, description: '', category: type === 'expense' ? 'Ingredientes' : 'general', reference: '' }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.description) { toast.error('Completa los campos requeridos'); return; }
    try {
      await axios.post(`${API}/admin/finances/transactions`, { ...form, type: txnType }, { withCredentials: true, headers: getHeaders() });
      toast.success(txnType === 'payment' ? 'Pago registrado' : 'Gasto registrado');
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta transaccion?')) return;
    try {
      await axios.delete(`${API}/admin/finances/transactions/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Eliminada');
      fetchData();
    } catch { toast.error('Error'); }
  };

  if (loading && !summary) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-finances-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#46371c]">Finanzas</h1>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => openDialog('payment')} className="bg-[#a0d5ba] text-[#46371c] hover:bg-[#82ba9e] rounded-full font-body" data-testid="add-payment-btn">
            <ArrowUpCircle className="w-4 h-4 mr-2" /> Registrar Pago
          </Button>
          <Button onClick={() => openDialog('expense')} variant="outline" className="border-[#46371c] text-[#46371c] rounded-full font-body" data-testid="add-expense-btn">
            <ArrowDownCircle className="w-4 h-4 mr-2" /> Registrar Gasto
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="finance-summary">
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-5">
            <div className="w-10 h-10 rounded-xl bg-[#a0d5ba]/20 flex items-center justify-center mb-3"><DollarSign className="w-5 h-5 text-[#46371c]" /></div>
            <p className="font-body text-xs text-[#75664d]">Ingresos Pedidos</p>
            <p className="font-heading text-2xl font-bold text-[#46371c]">{formatPrice(summary?.total_order_income || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-5">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center mb-3"><TrendingUp className="w-5 h-5 text-green-700" /></div>
            <p className="font-body text-xs text-[#75664d]">Pagos Recibidos</p>
            <p className="font-heading text-2xl font-bold text-green-700">{formatPrice(summary?.total_payments || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-5">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center mb-3"><TrendingDown className="w-5 h-5 text-red-600" /></div>
            <p className="font-body text-xs text-[#75664d]">Gastos Totales</p>
            <p className="font-heading text-2xl font-bold text-red-600">{formatPrice(summary?.total_expenses || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#a0d5ba] bg-[#eef6f2]">
          <CardContent className="p-5">
            <div className="w-10 h-10 rounded-xl bg-[#a0d5ba]/30 flex items-center justify-center mb-3"><DollarSign className="w-5 h-5 text-[#46371c]" /></div>
            <p className="font-body text-xs text-[#75664d]">Balance</p>
            <p className="font-heading text-2xl font-bold text-[#46371c]">{formatPrice(summary?.balance || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="border border-[#e5dfd3] bg-[#fcfbf9] overflow-hidden">
        <div className="p-4 border-b border-[#e5dfd3] flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-[#46371c]">Transacciones</h2>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#75664d]" />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-36 rounded-xl border-[#e5dfd3] h-8" data-testid="txn-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="payment">Pagos</SelectItem>
                <SelectItem value="expense">Gastos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {transactions.length === 0 ? (
          <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay transacciones registradas</p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[650px]">
              <TableHeader>
                <TableRow className="bg-[#f4f1ea]">
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Tipo</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Descripcion</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Categoria</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Monto</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Fecha</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map(txn => (
                  <TableRow key={txn.id} data-testid={`txn-row-${txn.id}`}>
                    <TableCell>
                      <Badge className={txn.type === 'payment' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {txn.type === 'payment' ? 'Pago' : 'Gasto'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className="font-body text-sm text-[#46371c]">{txn.description}</p>
                      {txn.reference && <p className="font-body text-xs text-[#75664d]">Ref: {txn.reference}</p>}
                    </TableCell>
                    <TableCell className="font-body text-xs text-[#75664d]">{txn.category}</TableCell>
                    <TableCell className={`font-body text-sm font-semibold ${txn.type === 'payment' ? 'text-green-700' : 'text-red-600'}`}>
                      {txn.type === 'payment' ? '+' : '-'}{formatPrice(txn.amount)}
                    </TableCell>
                    <TableCell className="font-body text-xs text-[#75664d]">{new Date(txn.created_at).toLocaleDateString('es-CO')}</TableCell>
                    <TableCell>
                      <button onClick={() => handleDelete(txn.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-txn-${txn.id}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{txnType === 'payment' ? 'Registrar Pago' : 'Registrar Gasto'}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">{txnType === 'payment' ? 'Registra un pago recibido' : 'Registra un gasto del negocio'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Monto (COP) *</label>
              <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: Number(e.target.value) }))} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="txn-amount-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Descripcion *</label>
              <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="txn-desc-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Categoria</label>
              {txnType === 'expense' ? (
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="rounded-xl border-[#e5dfd3]" data-testid="txn-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="Ej: Pedido KR-XXXX" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="txn-category-input" />
              )}
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Referencia</label>
              <input value={form.reference} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} placeholder="Numero de factura, recibo, etc." className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="txn-ref-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
              <Button type="submit" className={`flex-1 rounded-full font-body ${txnType === 'payment' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-500 text-white hover:bg-red-600'}`} data-testid="save-txn-btn">
                {txnType === 'payment' ? 'Registrar Pago' : 'Registrar Gasto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
